import { useState, useCallback, useMemo } from "react";
import { useTrackers } from "@/api/tracker";
import { useDashboards } from "@/api/dashboard";
import { useWidgets } from "@/api/widget";
import { useEnrichments } from "@/api/enrichment";
import { useAlerts } from "@/api/alert";
import { getBackendWidgetType } from "@/models/widget-v2";

// ============================================================================
// Types
// ============================================================================

export type EntityType = "tracker" | "dashboard" | "widget" | "enrichment" | "alert";

export interface MentionableEntity {
  id: string;
  type: EntityType;
  name: string;
  /** Optional description or metadata */
  description?: string;
  /** Optional icon override */
  icon?: string;
}

export interface EntityMentionState {
  isOpen: boolean;
  query: string;
  startIndex: number;
  position: { top: number; left: number };
  highlightedIndex: number;
}

export interface UseEntityMentionsOptions {
  /** Trigger character for mention (default: "@") */
  triggerChar?: string;
  /** Callback when an entity is selected */
  onSelect?: (entity: MentionableEntity) => void;
  /** Whether to include widgets from dashboards */
  includeWidgets?: boolean;
}

export interface UseEntityMentionsReturn {
  /** Current mention state */
  mentionState: EntityMentionState;
  /** All available entities */
  entities: MentionableEntity[];
  /** Filtered entities based on current query */
  filteredEntities: MentionableEntity[];
  /** Whether data is still loading */
  isLoading: boolean;
  /** Handle input change to detect trigger */
  handleInputChange: (
    value: string,
    cursorPosition: number,
    inputEl: HTMLInputElement | HTMLTextAreaElement | null
  ) => void;
  /** Handle keyboard navigation */
  handleKeyDown: (e: React.KeyboardEvent) => boolean;
  /** Select an entity from the list (pass displayValue and mentionMap, returns internalValue) */
  selectEntity: (entity: MentionableEntity, currentDisplayValue: string, currentMentionMap?: Map<string, MentionData>) => string;
  /** Close the mention popover */
  closeMention: () => void;
  /** Set highlighted index */
  setHighlightedIndex: (index: number) => void;
}

// ============================================================================
// Constants
// ============================================================================

const initialState: EntityMentionState = {
  isOpen: false,
  query: "",
  startIndex: -1,
  position: { top: 0, left: 0 },
  highlightedIndex: 0,
};

// Characters that should stop mention detection
const STOP_CHARS = new Set([" ", "\n", "\t", "[", "]"]);


// ============================================================================
// Conversion Functions (Internal ↔ Display)
// ============================================================================

/**
 * Regex to match entity mentions in internal value format: @[type:id:name]
 */
const MENTION_REGEX = /@\[(\w+):([^:]+):([^\]]+)\]/g;

/**
 * Data stored for each mention to enable conversion back to internal format
 */
export interface MentionData {
  type: EntityType;
  id: string;
  name: string;
}

/**
 * Convert internal value (with @[type:id:name]) to display value (with @name)
 * Also returns a mapping of display names to their full mention data
 */
export function internalToDisplay(internalValue: string): {
  displayValue: string;
  mentionMap: Map<string, MentionData>;
} {
  const mentionMap = new Map<string, MentionData>();
  
  const displayValue = internalValue.replace(MENTION_REGEX, (match, type, id, name) => {
    const displayName = `@${name}`;
    mentionMap.set(displayName, { type: type as EntityType, id, name });
    return displayName;
  });
  
  return { displayValue, mentionMap };
}

/**
 * Escape special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Convert display value back to internal value using the mention mapping
 */
export function displayToInternal(
  displayValue: string,
  mentionMap: Map<string, MentionData>
): string {
  let result = displayValue;
  
  // Sort by length descending to replace longer matches first (avoid partial replacements)
  const sortedEntries = Array.from(mentionMap.entries()).sort((a, b) => b[0].length - a[0].length);
  
  for (const [displayName, data] of sortedEntries) {
    // Simple string replacement - since displayName starts with @, it's unique enough
    const internalFormat = `@[${data.type}:${data.id}:${data.name}]`;
    // Replace all occurrences
    while (result.includes(displayName)) {
      result = result.replace(displayName, internalFormat);
    }
  }
  
  return result;
}

/**
 * Parse display value to find mention chips for rendering
 * Returns array of mentions with their positions in the display string
 */
export function parseDisplayMentions(
  displayValue: string,
  mentionMap: Map<string, MentionData>
): Array<{
  type: EntityType;
  id: string;
  name: string;
  displayName: string;
  startIndex: number;
  endIndex: number;
}> {
  const mentions: Array<{
    type: EntityType;
    id: string;
    name: string;
    displayName: string;
    startIndex: number;
    endIndex: number;
  }> = [];
  
  // Search for each known mention in the display value
  // Sort by length descending to find longer matches first
  const sortedEntries = Array.from(mentionMap.entries()).sort((a, b) => b[0].length - a[0].length);
  
  // Track which positions have already been matched to avoid overlaps
  const matchedRanges: Array<{ start: number; end: number }> = [];
  
  for (const [displayName, data] of sortedEntries) {
    let searchStart = 0;
    let index: number;
    
    while ((index = displayValue.indexOf(displayName, searchStart)) !== -1) {
      // Check if this position overlaps with an already matched range
      const overlaps = matchedRanges.some(
        range => index < range.end && index + displayName.length > range.start
      );
      
      if (!overlaps) {
        mentions.push({
          type: data.type,
          id: data.id,
          name: data.name,
          displayName,
          startIndex: index,
          endIndex: index + displayName.length,
        });
        matchedRanges.push({ start: index, end: index + displayName.length });
      }
      
      searchStart = index + 1;
    }
  }
  
  // Sort mentions by startIndex for proper rendering
  mentions.sort((a, b) => a.startIndex - b.startIndex);
  
  return mentions;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format an entity mention for insertion into text
 * Format: @[type:id:displayName]
 */
export function formatEntityMention(entity: MentionableEntity): string {
  return `@[${entity.type}:${entity.id}:${entity.name}]`;
}

/**
 * Parse entity mentions from text
 * Returns array of parsed mentions with their positions
 */
export function parseEntityMentions(text: string): Array<{
  type: EntityType;
  id: string;
  name: string;
  startIndex: number;
  endIndex: number;
}> {
  const regex = /@\[(\w+):([^:]+):([^\]]+)\]/g;
  const mentions: Array<{
    type: EntityType;
    id: string;
    name: string;
    startIndex: number;
    endIndex: number;
  }> = [];

  let match;
  while ((match = regex.exec(text)) !== null) {
    mentions.push({
      type: match[1] as EntityType,
      id: match[2],
      name: match[3],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  return mentions;
}

/**
 * Get display name for entity type
 */
export function getEntityTypeLabel(type: EntityType): string {
  const labels: Record<EntityType, string> = {
    tracker: "Tracker",
    dashboard: "Dashboard",
    widget: "Widget",
    enrichment: "Enrichissement",
    alert: "Alerte",
  };
  return labels[type] || type;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for managing "@" entity mention autocomplete in text inputs
 *
 * Features:
 * - Detects "@" trigger character
 * - Fetches all available entities (trackers, dashboards, etc.)
 * - Filters entities based on typed query
 * - Keyboard navigation (ArrowUp/Down, Enter, Escape)
 * - Calculates popover position based on cursor
 */
export const useEntityMentions = (
  options: UseEntityMentionsOptions = {}
): UseEntityMentionsReturn => {
  const { triggerChar = "@", onSelect, includeWidgets = false } = options;

  const [mentionState, setMentionState] = useState<EntityMentionState>(initialState);

  // Fetch all entity types
  const { data: trackers = [], isLoading: trackersLoading } = useTrackers();
  const { data: dashboards = [], isLoading: dashboardsLoading } = useDashboards();
  const { data: widgets = [], isLoading: widgetsLoading } = useWidgets();
  const { data: enrichments = [], isLoading: enrichmentsLoading } = useEnrichments();
  const { data: alerts = [], isLoading: alertsLoading } = useAlerts();

  const isLoading =
    trackersLoading ||
    dashboardsLoading ||
    (includeWidgets ? widgetsLoading : false) ||
    enrichmentsLoading ||
    alertsLoading;

  // Combine all entities into a unified list
  const entities = useMemo((): MentionableEntity[] => {
    const result: MentionableEntity[] = [];

    // Add trackers
    trackers.forEach((tracker) => {
      const name = tracker.name || 
        (typeof tracker.metadata?.name === 'string' ? tracker.metadata.name : null) || 
        `Tracker ${tracker.id.slice(0, 8)}`;
      result.push({
        id: tracker.id,
        type: "tracker",
        name,
        description: tracker.source || tracker.renderer,
      });
    });

    // Add dashboards
    dashboards.forEach((dashboard) => {
      result.push({
        id: dashboard.id,
        type: "dashboard",
        name: dashboard.name || `Dashboard ${dashboard.id.slice(0, 8)}`,
        description: dashboard.description || undefined,
      });
    });

    // Optionally add widgets (fetched separately, not from dashboards)
    if (includeWidgets) {
      widgets.forEach((widget) => {
        result.push({
          id: widget.id || "",
          type: "widget",
          name: widget.name || widget.metadata?.name || `Widget ${(widget.id || "").slice(0, 8)}`,
          description: getBackendWidgetType(widget, undefined as any) || undefined,
        });
      });
    }

    // Add enrichments
    enrichments.forEach((enrichment) => {
      result.push({
        id: enrichment.id,
        type: "enrichment",
        name: enrichment.name || `Enrichissement ${enrichment.id.slice(0, 8)}`,
        description: enrichment.type || enrichment.renderer,
      });
    });

    // Add alerts
    alerts.forEach((alert) => {
      result.push({
        id: alert.id,
        type: "alert",
        name: alert.name || `Alerte ${alert.id.slice(0, 8)}`,
        description: alert.description || undefined,
      });
    });

    return result;
  }, [trackers, dashboards, widgets, enrichments, alerts, includeWidgets]);

  // Filter entities based on current query
  const filteredEntities = useMemo(() => {
    if (!mentionState.query) {
      return entities;
    }
    const lowerQuery = mentionState.query.toLowerCase();
    return entities.filter(
      (entity) =>
        entity.name.toLowerCase().includes(lowerQuery) ||
        entity.type.toLowerCase().includes(lowerQuery) ||
        entity.description?.toLowerCase().includes(lowerQuery)
    );
  }, [entities, mentionState.query]);

  // Calculate cursor position from input element
  const getCursorPosition = useCallback(
    (inputEl: HTMLInputElement | HTMLTextAreaElement | null): { top: number; left: number } => {
      if (!inputEl) {
        return { top: 0, left: 0 };
      }

      // Get input element's bounding rect (relative to viewport)
      const rect = inputEl.getBoundingClientRect();

      // Return viewport-relative coordinates for the top of the input
      // This allows the dropdown to open upward from this position
      return {
        top: rect.top,
        left: rect.left,
      };
    },
    []
  );

  // Handle input change to detect trigger
  const handleInputChange = useCallback(
    (
      value: string,
      cursorPosition: number,
      inputEl: HTMLInputElement | HTMLTextAreaElement | null
    ) => {
      // Find the start of the current mention (if any)
      let mentionStart = -1;
      let query = "";

      // Look backwards from cursor to find "@"
      for (let i = cursorPosition - 1; i >= 0; i--) {
        const char = value[i];

        // Stop if we hit a stop character
        if (STOP_CHARS.has(char)) {
          break;
        }

        // Found the trigger character
        if (char === triggerChar) {
          mentionStart = i;
          query = value.substring(i + 1, cursorPosition);
          break;
        }
      }

      if (mentionStart >= 0) {
        const position = getCursorPosition(inputEl);
        setMentionState({
          isOpen: true,
          query,
          startIndex: mentionStart,
          position,
          highlightedIndex: 0,
        });
      } else {
        if (mentionState.isOpen) {
          setMentionState(initialState);
        }
      }
    },
    [triggerChar, getCursorPosition, mentionState.isOpen]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (!mentionState.isOpen) {
        return false;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setMentionState((prev) => ({
            ...prev,
            highlightedIndex: Math.min(
              prev.highlightedIndex + 1,
              Math.max(filteredEntities.length - 1, 0)
            ),
          }));
          return true;

        case "ArrowUp":
          e.preventDefault();
          setMentionState((prev) => ({
            ...prev,
            highlightedIndex: Math.max(prev.highlightedIndex - 1, 0),
          }));
          return true;

        case "Enter":
        case "Tab":
          if (filteredEntities.length > 0) {
            e.preventDefault();
            return true; // Signal that selection should happen
          }
          return false;

        case "Escape":
          e.preventDefault();
          setMentionState(initialState);
          return true;

        default:
          return false;
      }
    },
    [mentionState.isOpen, filteredEntities.length]
  );

  /**
   * Select an entity and return the new internal value.
   * 
   * @param entity - The entity to insert
   * @param currentDisplayValue - The current display value (with @name format)
   * @param currentMentionMap - The current mention map for conversion
   * @returns The new internal value (with @[type:id:name] format)
   */
  const selectEntity = useCallback(
    (
      entity: MentionableEntity,
      currentDisplayValue: string,
      currentMentionMap?: Map<string, MentionData>
    ): string => {
      const { startIndex, query } = mentionState;

      // Replace @query with @name in display value
      const beforeMention = currentDisplayValue.substring(0, startIndex);
      const afterMention = currentDisplayValue.substring(startIndex + 1 + query.length);
      const displayMention = `@${entity.name}`;
      const newDisplayValue = beforeMention + displayMention + " " + afterMention;

      // Create updated mention map with the new entity
      const updatedMap = new Map(currentMentionMap || new Map());
      updatedMap.set(displayMention, {
        type: entity.type,
        id: entity.id,
        name: entity.name,
      });

      // Convert display value to internal value
      const newInternalValue = displayToInternal(newDisplayValue, updatedMap);

      onSelect?.(entity);
      setMentionState(initialState);

      return newInternalValue;
    },
    [mentionState, onSelect]
  );

  // Close the mention popover
  const closeMention = useCallback(() => {
    setMentionState(initialState);
  }, []);

  // Set highlighted index
  const setHighlightedIndex = useCallback((index: number) => {
    setMentionState((prev) => ({
      ...prev,
      highlightedIndex: index,
    }));
  }, []);

  return {
    mentionState,
    entities,
    filteredEntities,
    isLoading,
    handleInputChange,
    handleKeyDown,
    selectEntity,
    closeMention,
    setHighlightedIndex,
  };
};

export default useEntityMentions;
